/**
 * ChartPackagesByCarrier - Componente que exibe distribuição de pacotes por transportadora
 * 
 * Este componente processa dados reais de trips e calcula a distribuição de pacotes
 * por transportadora, mostrando percentuais e quantidades reais.
 * 
 * @example
 * // Dados de entrada (trips):
 * const trips = [
 *   { id: 1, carrier_name: 'Agregado', package_count: 100 },
 *   { id: 2, carrier_name: 'Agregado', package_count: 50 },
 *   { id: 3, carrier_name: 'JK', package_count: 75 },
 *   { id: 4, carrier_name: 'BBM', package_count: 25 }
 * ];
 * 
 * // Resultado no gráfico:
 * // Total: 250 pacotes
 * // Agregado: 60% (150 pacotes)
 * // JK: 30% (75 pacotes)  
 * // BBM: 10% (25 pacotes)
 * 
 * // Integração com filtros:
 * // O componente recebe trips já filtradas do sistema de filtros,
 * // então mostra apenas a distribuição dos dados visíveis
 * 
 * @param {Array} trips - Array de objetos trip com carrier_name e package_count
 */
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { PieChart } from '@mui/x-charts/PieChart';
import PropTypes from 'prop-types';
import * as React from 'react';

// Cores para os gráficos
const colors = [
  'hsl(220, 20%, 65%)',
  'hsl(220, 20%, 42%)',
  'hsl(220, 20%, 35%)',
  'hsl(220, 20%, 25%)',
  'hsl(220, 20%, 15%)',
  'hsl(220, 20%, 55%)',
  'hsl(220, 20%, 75%)',
  'hsl(220, 20%, 85%)',
];

// Função para processar dados das trips e calcular distribuição de pacotes
function processCarrierData(trips) {
  if (!trips || trips.length === 0) {
    return { chartData: [], carrierStats: [], totalPackages: 0 };
  }

  // Agrupa pacotes por transportadora
  const carrierPackages = trips.reduce((acc, trip) => {
    const carrierName = trip.carrier_name || 'Sem Transportadora';
    const packageCount = trip.package_count || 0;

    if (!acc[carrierName]) {
      acc[carrierName] = 0;
    }
    acc[carrierName] += packageCount;
    return acc;
  }, {});

  // Calcula total de pacotes
  const totalPackages = Object.values(carrierPackages).reduce((sum, count) => sum + count, 0);

  // Ordena por quantidade e converte para array
  const sortedCarriers = Object.entries(carrierPackages)
    .sort(([, a], [, b]) => b - a)
    .map(([name, count], index) => ({
      name,
      count,
      percentage: totalPackages > 0 ? Math.round((count / totalPackages) * 100) : 0,
      color: colors[index % colors.length],
    }));

  // Dados para o gráfico de pizza
  const chartData = sortedCarriers.map(carrier => ({
    label: carrier.name,
    value: carrier.count,
  }));

  return {
    chartData,
    carrierStats: sortedCarriers,
    totalPackages,
  };
}

const StyledText = styled('text', {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontSize: theme.typography.h5.fontSize,
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <React.Fragment>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </React.Fragment>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

// Função para formatar números
function formatNumber(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num.toString();
}

export default function ChartPackagesByCarrier({ trips = [] }) {
  // Processa os dados das trips
  const { chartData, carrierStats, totalPackages } = React.useMemo(() =>
    processCarrierData(trips), [trips]
  );

  // Se não há dados, mostra mensagem
  if (!trips || trips.length === 0 || totalPackages === 0) {
    return (
      <Card
        variant="outlined"
        sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
      >
        <CardContent>
          <Typography component="h2" variant="subtitle2" sx={{ mb: 2 }}>
            Pacotes por Transportadora
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nenhum pacote encontrado
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
    >
      <CardContent>
        <Typography component="h2" variant="subtitle2">
          Pacotes por Transportadora
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart
            colors={colors}
            margin={{
              left: 80,
              right: 80,
              top: 80,
              bottom: 80,
            }}
            series={[
              {
                data: chartData,
                innerRadius: 75,
                outerRadius: 100,
                paddingAngle: 0,
                highlightScope: { fade: 'global', highlight: 'item' },
              },
            ]}
            height={260}
            width={260}
            hideLegend
          >
            <PieCenterLabel
              primaryText={formatNumber(totalPackages)}
              secondaryText="Total"
            />
          </PieChart>
        </Box>

        {/* Lista de transportadoras com progresso */}
        {carrierStats.map((carrier, index) => (
          <Stack
            key={carrier.name}
            direction="row"
            sx={{ alignItems: 'center', gap: 2, pb: 2 }}
          >
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                  {carrier.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {carrier.percentage}% ({carrier.count})
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                aria-label={`Pacotes de ${carrier.name}: ${carrier.count}`}
                value={carrier.percentage}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: carrier.color,
                  },
                }}
              />
            </Stack>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

ChartPackagesByCarrier.propTypes = {
  /**
   * Array of trip objects to calculate package distribution
   */
  trips: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    carrier_name: PropTypes.string,
    package_count: PropTypes.number,
  })),
}; 